/**
 * AI询单（需求订单）相关接口
 */
import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 任务类型 */
export type AiTaskType =
  | "book_train"
  | "book_flight"
  | "book_hotel"
  | "book_car"
  | "book_guide"
  | "book_tour";

/** 订单状态码 */
export const ORDER_STATUS_MAP: Record<number, { label: string; step: number }> = {
  [-5]: { label: "无效", step: -1 },
  [-3]: { label: "已退款", step: -1 },
  [-1]: { label: "已取消", step: -1 },
  [0]: { label: "待处理", step: 0 },
  [1]: { label: "处理中", step: 1 },
  [2]: { label: "已成单", step: 3 },
};

// ==================== 支付接口类型 ====================

/** 支付请求 - 乘客信息 */
export interface AiOrderPayPassenger {
  name: string;
  id_type: string;
  id_no: string;
}

/** 微信JSAPI支付参数 */
export interface AiOrderPayParams {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
}

/** 支付接口响应 */
export interface AiOrderPayResponse {
  code: number;
  message: string;
  data: AiOrderPayParams;
}

// ==================== 支付接口函数 ====================

/**
 * POST /home/ai-order/pay
 * 发起询单支付（获取微信JSAPI支付签名参数）
 *
 * @param order_no 订单号
 * @param passenger_info 乘客信息列表（姓名、证件类型、证件号）
 * @returns 调起微信支付所需的6个参数 { appId, timeStamp, nonceStr, package, signType, paySign }
 */
export function fetchAiOrderPay(order_no: string, passenger_info: AiOrderPayPassenger[]) {
  return http.post<AiOrderPayResponse>("/home/ai-order/pay", {
    order_no,
    passenger_info,
  });
}

// ==================== 详情接口类型 ====================

/** 乘客信息 */
export interface AiOrderPassenger {
  name: string;
  id_no: string;
  id_type: string;
}

/** 商品/服务明细 */
export interface AiOrderGoods {
  id: number;
  order_id: number;
  order_no: string;
  type: "hotel" | "car" | "tour" | "guide" | "flight" | "train";
  name: string;
  contact: string;
  start_time: string;
  end_time: string;
  province: string;
  city: string;
  district: string;
  address: string;
  status: number;
  price: string;
  number: number;
  amount: string;
  scheduled_time: string;
  used_time: string | null;
}

/** 详情中的订单主体 */
export interface AiOrderDetailOrder {
  order_id: number;
  task_id: number;
  task_params: {
    days?: number;
    budget?: string;
    plan_id?: string;
    departure?: string;
    transport?: string;
    travelers?: number;
    waypoints?: string;
    destination?: string;
    travel_style?: string;
    [key: string]: any;
  };
  uid: number;
  order_no: string;
  status: number;
  order_amount: string;
  pay_amount: string;
  pay_type: number;
  pay_channel_no: string;
  pay_time: string | null;
  refund_amount: string;
  refund_type: number;
  refund_channel_no: string;
  refund_time: string | null;
  passenger_info: AiOrderPassenger[];
  phone: string;
  remark: string;
  create_time: string;
  update_time: string;
}

/** 详情接口响应 data */
export interface AiOrderDetailData {
  order: AiOrderDetailOrder;
  goods: AiOrderGoods[];
  task_params_text: string;
  route_info: string[];
}

export interface AiOrderDetailResponse {
  code: number;
  message: string;
  data: AiOrderDetailData;
}

// ==================== 详情接口函数 ====================

/**
 * GET /home/ai-order/detail
 * 获取AI询单详情
 *
 * @param params.task_id 记录ID，使用列表返回的 id 字段（必传其一）
 * @param params.order_no 订单号（可选）
 */
export function fetchAiOrderDetail(params: { task_id?: number | string; order_no?: string }) {
  return http.get<AiOrderDetailResponse>("/home/ai-order/detail", {
    params,
  });
}

/** 单条AI询单记录 */
export interface AiOrderItem {
  id: number;
  user_id: number;
  session_id: string;
  identity: string;
  task_type: AiTaskType;
  task_params: Record<string, any>;
  status: string; // "completed" | "pending" | "failed" 等
  order_status: number;
  round: number;
  tool_call_id: string;
  model: string;
  tokens_used: number;
  error_message: string | null;
  retry_count: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  order_no: string | null;
  order_id: number | null;
  pay_type: string | null;
  order_amount: number | null;
  pay_amount: number | null;
  task_params_text: string;
}

/** 列表接口响应 data */
export interface AiOrderListData {
  list: AiOrderItem[];
  count: number;
  page: number;
  limit: number;
}

export interface AiOrderListResponse {
  code: number;
  message: string;
  data: AiOrderListData;
}

/** 列表请求参数 */
export interface AiOrderListParams {
  page?: number;
  limit?: number;
  order_status?: number | string;
}

// ==================== 接口函数 ====================

/**
 * GET /home/ai-order/list
 * 获取AI询单列表
 *
 * @param order_status 状态：0=待处理，-1=取消，-3=退款，-5=无效，1=处理中，报价信息待确认，2=用户
 */
export function fetchAiOrderList(params: AiOrderListParams = {}) {
  return http.get<AiOrderListResponse>("/home/ai-order/list", {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      ...(params.order_status !== undefined && params.order_status !== ""
        ? { order_status: params.order_status }
        : {}),
    },
  });
}
